import Grid from '@mui/material/Grid';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MetricCard from 'components/MetricCard';
import { useDashboardTheme, ACCENT_COLOR, fmt, fmtBtc } from '../utils';

function KPICards({ summary, engineInfo, liveState, netMetrics, nicehashBalance, loading }) {
  const t = useDashboardTheme();
  // Engine remaining = (availableAmount - paidAmount) — the unused hash budget
  const engineAvailRaw = engineInfo?.availableAmount != null
    ? Math.max(0, (Number(engineInfo.availableAmount) || 0) - (Number(engineInfo.paidAmount) || 0))
    : null;
  const airdropPot = engineInfo?.airdropAmount ?? 0;
  const accumulatedBtc = liveState?.airdropMeter?.accumulatedBtc ?? 0;
  const totalAirdropPot = airdropPot + accumulatedBtc;
  const rawPlBtc = netMetrics?.operatorPnlBtc ?? summary?.operatorPnlBtc ?? null;
  const rawPlUsd = netMetrics?.operatorPnlUsd ?? summary?.operatorPnlUsd ?? null;

  // During live sessions, adjust Operator P&L by adding engine remaining balance
  // (engine cost is fully deducted but unused balance hasn't been returned yet)
  const isLive = liveState?.eventStatus === 'live';
  const engineAvail = isLive && engineAvailRaw != null
    ? engineAvailRaw + (Number(airdropPot) || 0)
    : 0;
  const plBtc = rawPlBtc != null ? rawPlBtc + engineAvail : null;
  const plUsd = rawPlUsd != null ? rawPlUsd : null; // USD adjustment would need rate, skip

  const nhBalanceBtc = nicehashBalance?.availableBtc ?? summary?.nicehashBalanceBtc ?? null;

  // RTP = total player earnings / net wagered × 100
  // Net wagered excludes refunded boss bids (only winning boss bid per cycle counts).
  // Live sessions: use earningsLeaderboard from Redis (real-time)
  // Ended sessions: fall back to totalPlayerEarningsUsd from PG snapshot
  const livePlayerEarnings = (liveState?.earningsLeaderboard || []).reduce(
    (s, e) => s + (Number(e.totalEarnings) || 0), 0
  );
  const totalPlayerEarnings = liveState
    ? livePlayerEarnings
    : Number(summary?.totalPlayerEarningsUsd) || 0;
  const hasEarningsData = liveState || summary?.totalPlayerEarningsUsd != null;
  // Prefer netWageredUsd (excludes refunded boss bids); fall back to gross handle
  const netWagered = summary?.netWageredUsd != null
    ? Number(summary.netWageredUsd)
    : Number(summary?.totalBidsUsd) || 0;
  const rtp = hasEarningsData && netWagered > 0
    ? (totalPlayerEarnings / netWagered) * 100
    : null;
  const rtpPlaceholder = !hasEarningsData ? 'No data' : 'No wagers';

  return (
    <MDBox mb={3}>
      <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
        <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: ACCENT_COLOR, flexShrink: 0 }} />
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: t.primaryText, fontSize: '0.95rem' }}>
          Key Metrics
        </MDTypography>
        <MDBox sx={{ flex: 1, height: '1px', background: t.divider, display: { xs: 'none', sm: 'block' } }} />
      </MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Engine Balance'
            tooltip='Unused hash budget remaining in the NiceHash engine (available minus paid). This amount will be returned when the engine stops.'
            value={engineAvailRaw != null ? `${fmtBtc(engineAvailRaw)} BTC` : null}
            placeholder={engineAvailRaw == null}
            placeholderText='No data'
            icon='account_balance_wallet'
            iconColor='info'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Airdrop Pot (Total)'
            tooltip='BTC reserved for the next airdrop distribution. Engine pot is held in the NiceHash engine; accumulated pot comes from block reward contributions.'
            value={`${fmtBtc(totalAirdropPot)} BTC`}
            subtitle={`Engine: ${fmtBtc(airdropPot)} + Accumulated: ${fmtBtc(accumulatedBtc)}`}
            icon='card_giftcard'
            iconColor='success'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Total Blocks Mined'
            tooltip='Number of cryptocurrency blocks mined by the NiceHash engine during this session, with the total reward value received.'
            value={summary ? `${summary.totalBlocksMined}` : null}
            placeholder={!summary}
            placeholderText='No data'
            subtitle={summary ? `${fmtBtc(summary.totalBlockRewardBtc)} BTC / $${fmt(summary.totalBlockRewardUsd)}` : ''}
            icon='diamond'
            iconColor='warning'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title={isLive && engineAvail > 0 ? 'Operator P&L (adj.)' : 'Operator P&L'}
            tooltip='Our profit/loss: Wagering Net (pure bid revenue) minus Engine Net Cost (deposits + refills minus returns). Block rewards and boss winnings are pass-through distributions and excluded.'
            value={plBtc != null ? `${fmtBtc(plBtc)} BTC` : null}
            placeholder={plBtc == null}
            placeholderText='No data'
            subtitle={isLive && engineAvail > 0 ? `Incl. ${fmtBtc(engineAvail)} BTC engine balance` : plUsd != null ? `$${fmt(plUsd)}` : ''}
            icon='trending_up'
            iconColor={plBtc != null && plBtc >= 0 ? 'success' : 'error'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Total Bids'
            tooltip='Total bids placed (normal + auto). Turnover shows gross USD handle (excludes boss bids).'
            value={summary ? `${summary.totalBidsCount}` : null}
            placeholder={!summary}
            placeholderText='No data'
            subtitle={summary ? `$${fmt(summary.totalBidsUsd)} USD turnover` : ''}
            icon='casino'
            iconColor='primary'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Player RTP'
            tooltip='Return To Player: total player earnings divided by net wagered, as a percentage. Values above 100% mean players earned more than they wagered.'
            value={rtp != null ? `${rtp.toFixed(2)}%` : null}
            placeholder={rtp == null}
            placeholderText={rtpPlaceholder}
            subtitle={rtp != null ? `$${fmt(totalPlayerEarnings)} returned / $${fmt(netWagered)} wagered` : ''}
            icon='percent'
            iconColor={rtp != null && rtp <= 100 ? 'success' : rtp != null ? 'error' : 'info'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Airdrops Distributed'
            tooltip='Number of completed airdrop distributions triggered when the airdrop meter fills. Shows total BTC and USD distributed to players.'
            value={summary ? `${summary.totalAirdropsCount}` : null}
            placeholder={!summary}
            placeholderText='No data'
            subtitle={summary ? `${fmtBtc(summary.totalAirdropsBtc)} BTC / $${fmt(summary.totalAirdropsUsd)}` : ''}
            icon='redeem'
            iconColor='info'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Boss Winnings Paid'
            tooltip='Total USD paid out to boss winners. The boss wins the boss reward share of each block mined while they hold the boss position.'
            value={summary ? `$${fmt(summary.totalBossWinningsUsd)}` : null}
            placeholder={!summary}
            placeholderText='No data'
            icon='military_tech'
            iconColor='warning'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='Jackpot'
            tooltip='Current jackpot pool accumulated from block rewards. Awarded under special conditions.'
            value={liveState ? `$${fmt(liveState.jackpot)}` : null}
            placeholder={!liveState}
            placeholderText='No data'
            icon='emoji_events'
            iconColor='error'
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title='NH Account Balance'
            tooltip='Total available balance across all engines on the NiceHash account. Shared across all active rooms.'
            value={nhBalanceBtc != null ? `${fmtBtc(nhBalanceBtc)} BTC` : null}
            placeholder={nhBalanceBtc == null}
            placeholderText='Unavailable'
            subtitle={nicehashBalance?.fetchedAt ? `at ${new Date(nicehashBalance.fetchedAt).toLocaleTimeString()}` : summary?.nicehashBalanceFetchedAt ? 'At session close' : ''}
            icon='account_balance'
            iconColor='dark'
            loading={loading}
          />
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default KPICards;
